import React, { useEffect, useState } from "react";
import { TrendingUp, Banknote, AlertTriangle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard, RiskBadge } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";

export default function Analytics() {
  const [kpis, setKpis] = useState({});
  const [revenue, setRevenue] = useState([]);
  const [denial, setDenial] = useState([]);
  const [topDenied, setTopDenied] = useState([]);
  const [burnout, setBurnout] = useState([]);

  useEffect(() => {
    api.get("/analytics/kpis").then(r=>setKpis(r.data));
    api.get("/analytics/revenue-trend").then(r=>setRevenue(r.data));
    api.get("/analytics/denial-by-payer").then(r=>setDenial(r.data));
    api.get("/analytics/top-denied-cpts").then(r=>setTopDenied(r.data));
    api.get("/analytics/burnout").then(r=>setBurnout(r.data));
  }, []);

  const cptCols = [
    { key: "cpt", label: "CPT", render: (r)=><span className="font-mono text-brand-primary">{r.cpt}</span> },
    { key: "description", label: "Description" },
    { key: "denials", label: "Denials", render: (r)=><span className="font-semibold text-red-600">{r.denials}</span> },
  ];

  const burnoutCols = [
    { key: "provider", label: "Provider" },
    { key: "encounters_per_day", label: "Encounters/day" },
    { key: "doc_time_min", label: "Doc time (min)" },
    { key: "risk", label: "Risk", render: (r)=><RiskBadge risk={r.risk} /> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 12" title="Analytics Dashboard"
        subtitle="KPIs, revenue trend, denial intelligence and burnout index." testid="analytics-page" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={TrendingUp} label="Encounters / Month" value={kpis.encounters_this_month ?? "—"} hint="This month" testid="kpi-encounters" />
        <KpiCard icon={Banknote} label="Claims Submitted" value={kpis.claims_submitted ?? "—"} hint="Total" testid="kpi-claims" />
        <KpiCard icon={AlertTriangle} label="Denial Rate" value={`${kpis.denial_rate ?? "—"}%`} hint="Average" accent testid="kpi-denial" />
        <KpiCard icon={Clock} label="Avg Days to Payment" value={kpis.avg_days_to_payment ?? "—"} hint="Last 90 days" testid="kpi-dtp" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <SectionCard title="Revenue Collected — Last 12 Months" testid="rev-chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9E3F5" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6E6380" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6E6380" }} tickFormatter={(v)=>`$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v)=>`$${v.toLocaleString()}`} />
                <Line type="monotone" dataKey="revenue" stroke="#4A0E8F" strokeWidth={2.5} dot={{ fill: "#9B59B6", r: 3 }} activeDot={{ r: 6, fill: "#0D7377" }} />
              </LineChart>
            </ResponsiveContainer>
          </SectionCard>
        </div>
        <SectionCard title="Denial Rate by Payer" testid="denial-chart">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={denial}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E9E3F5" />
              <XAxis dataKey="payer" tick={{ fontSize: 10, fill: "#6E6380" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6E6380" }} tickFormatter={(v)=>`${v}%`} />
              <Tooltip formatter={(v)=>`${v}%`} />
              <Bar dataKey="rate" fill="#9B59B6" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Top 10 Denied CPT Codes" testid="top-denied-section">
          <DataTable data={topDenied} columns={cptCols} searchKeys={["cpt","description"]} testid="top-denied-table" />
        </SectionCard>
        <SectionCard title="Burnout Risk Index" subtitle="Encounters/day × documentation time" testid="burnout-section">
          <DataTable data={burnout} columns={burnoutCols} searchKeys={["provider","risk"]} testid="burnout-table" />
        </SectionCard>
      </div>
    </>
  );
}
